import type { Analytics } from 'firebase/analytics'
import {
  getAnalytics,
  initializeAnalytics,
  isSupported,
  logEvent,
} from 'firebase/analytics'
import { getApps, initializeApp } from 'firebase/app'
import { usePathname, useSearchParams } from 'next/navigation'
import React, { Suspense, useEffect } from 'react'

import {
  FIREBASE_CONFIG_APIKEY,
  FIREBASE_CONFIG_APP_ID,
  FIREBASE_CONFIG_AUTH_DOMAIN,
  FIREBASE_CONFIG_MEASUREMENT_ID,
  FIREBASE_CONFIG_MESSAGING_SENDER_ID,
  FIREBASE_CONFIG_PROJECT_ID,
  FIREBASE_CONFIG_STORAGE_BUCKET,
} from '@goodfellas/environment'

const firebaseConfig = {
  apiKey: FIREBASE_CONFIG_APIKEY,
  authDomain: FIREBASE_CONFIG_AUTH_DOMAIN,
  projectId: FIREBASE_CONFIG_PROJECT_ID,
  storageBucket: FIREBASE_CONFIG_STORAGE_BUCKET,
  messagingSenderId: FIREBASE_CONFIG_MESSAGING_SENDER_ID,
  appId: FIREBASE_CONFIG_APP_ID,
  measurementId: FIREBASE_CONFIG_MEASUREMENT_ID,
}

let analyticsPromise: Promise<Analytics | null> | null = null

// Inicializa o Firebase Analytics uma única vez, apenas no browser e quando o
// ambiente suporta (SSR, browsers sem IndexedDB/cookies etc. resolvem null).
export function getFirebaseAnalytics(): Promise<Analytics | null> {
  if (typeof window === 'undefined') return Promise.resolve(null)
  if (!FIREBASE_CONFIG_APIKEY || !FIREBASE_CONFIG_MEASUREMENT_ID) {
    return Promise.resolve(null)
  }

  if (!analyticsPromise) {
    analyticsPromise = isSupported()
      .then((supported) => {
        if (!supported) return null
        const app = getApps().length
          ? getApps()[0]
          : initializeApp(firebaseConfig)
        try {
          // page_view automático desligado: o pageview é registrado
          // manualmente a cada troca de rota (SPA) pelo componente abaixo.
          return initializeAnalytics(app, {
            config: { send_page_view: false },
          })
        } catch {
          // Fast Refresh pode reexecutar este módulo com o Analytics já
          // inicializado para o app; nesse caso reaproveita a instância.
          return getAnalytics(app)
        }
      })
      .catch(() => null)
  }

  return analyticsPromise
}

// Registra um evento sem o chamador precisar lidar com a inicialização
// assíncrona; vira no-op fora do browser ou sem configuração do Firebase.
export function logAnalyticsEvent(
  eventName: string,
  eventParams?: Record<string, unknown>,
): void {
  getFirebaseAnalytics().then((analytics) => {
    if (analytics) logEvent(analytics, eventName, eventParams)
  })
}

export function FirebaseAnalyticsProvider({
  children,
}: {
  children: React.ReactNode
}) {
  useEffect(() => {
    getFirebaseAnalytics()
  }, [])

  return (
    <>
      <SuspendedFirebaseAnalyticsPageView />
      {children}
    </>
  )
}

function FirebaseAnalyticsPageView() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Track pageviews
  useEffect(() => {
    if (pathname) {
      let url = window.origin + pathname
      if (searchParams.toString()) {
        url = `${url}?${searchParams.toString()}`
      }

      logAnalyticsEvent('page_view', {
        page_location: url,
        page_path: pathname,
        page_title: document.title,
      })
    }
  }, [pathname, searchParams])

  return null
}

// Wrap FirebaseAnalyticsPageView in Suspense to avoid the useSearchParams
// usage above from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedFirebaseAnalyticsPageView() {
  return (
    <Suspense fallback={null}>
      <FirebaseAnalyticsPageView />
    </Suspense>
  )
}
